import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BrandMark } from "@/components/BrandMark";
import { useI18n } from "@/context/I18nContext";
import { HeartPulse, Brain, Users, ScrollText, Sparkles, Shield, MessageCircleHeart, ArrowRight, Apple, Smartphone } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1774625724113-12a774fce1e3";

const FEATURE_ICONS = [MessageCircleHeart, Brain, ScrollText, Sparkles, Users, Shield];

export default function Landing() {
  const { t } = useI18n();
  const features = FEATURE_ICONS.map((icon, i) => ({
    icon, title: t(`landing.f${i + 1}t`), desc: t(`landing.f${i + 1}d`),
  }));
  return (
    <div className="grain min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/70 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <BrandMark className="h-9 w-9" />
            <span className="font-serif text-xl font-semibold">EverMind</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><LanguageSwitcher compact testid="landing-language-switcher" /></div>
            <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-login-button">{t("common.signIn")}</Button></Link>
            <Link to="/register"><Button className="rounded-full" data-testid="nav-register-button">{t("common.getStarted")}</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-background/70 dark:bg-background/80" />
        </div>
        <div className="relative mx-auto max-w-6xl px-6 py-28 sm:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                      className="max-w-2xl">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-sm text-foreground/80">
              {t("landing.badge")}
            </span>
            <h1 className="mt-6 font-serif text-4xl font-semibold tracking-tight leading-tight sm:text-5xl lg:text-6xl">
              {t("landing.heroTitle")}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              {t("landing.heroDesc")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="group rounded-full px-7 transition-transform duration-300 hover:-translate-y-0.5" data-testid="hero-get-started-button">
                  {t("landing.begin")} <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/login"><Button size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-signin-button">{t("landing.haveAccount")}</Button></Link>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="landing-coming-soon">
              <Apple className="h-4 w-4" strokeWidth={1.5} />
              <Smartphone className="h-4 w-4" strokeWidth={1.5} />
              <span>{t("landing.comingSoon")}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <div className="max-w-xl">
          <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl">{t("landing.featuresTitle")}</h2>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06 }}
                        className="ambient-shadow rounded-3xl border border-border/60 bg-card p-7">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10">
                <f.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="mt-5 font-serif text-xl">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-28">
        <div className="ambient-shadow rounded-3xl border border-border/60 bg-gradient-to-b from-accent/40 to-card p-12 text-center">
          <h2 className="font-serif text-3xl font-semibold sm:text-4xl">{t("landing.finalTitle")}</h2>
          <p className="mx-auto mt-4 max-w-md text-muted-foreground">{t("landing.finalDesc")}</p>
          <Link to="/register"><Button size="lg" className="mt-8 rounded-full px-8" data-testid="cta-get-started-button">{t("landing.finalCta")}</Button></Link>
          <p className="mt-6 text-xs text-muted-foreground">{t("landing.crisis")}</p>
        </div>
      </section>

      <footer className="border-t border-border/40 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 text-xs text-muted-foreground sm:flex-row">
          <span>© {new Date().getFullYear()} EverMind</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">{t("privacy.policyLink")}</Link>
            <Link to="/support" className="hover:text-foreground" data-testid="footer-support-link">Support</Link>
            <Link to="/impressum" className="hover:text-foreground" data-testid="footer-impressum-link">Impressum</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
